/**
 * MIR Review Queue — Sprint 3 Step 3.1 (Minimal Frontend)
 *
 * Walks MIR documents through the physical-movement workflow:
 *   draft → issued → received → inspected → accepted | rejected
 *
 * Backend endpoints:
 *   GET  /api/pece/mir            (filter project/status)
 *   GET  /api/pece/mir/pending
 *   GET  /api/pece/mir/{id}
 *   POST /api/pece/mir/{id}/issue        (sets qty_issued)
 *   POST /api/pece/mir/{id}/receive      (sets qty_received, delivery_note_ref)
 *   POST /api/pece/mir/{id}/inspect      (QA on-site)
 *   POST /api/pece/mir/{id}/accept       (qty_accepted; emits consumed_partial)
 *   POST /api/pece/mir/{id}/reject       (reason)
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  PackageCheck, RefreshCw, Loader2, ListChecks, Activity, FileText,
  Check, X, AlertTriangle, ChevronDown, ChevronUp, Truck, Microscope,
  PackageOpen, ArrowDownToLine, Building, Plus,
} from 'lucide-react';

const STATUS_META = {
  draft:     { label: 'مسودة',          cls: 'bg-gray-100 text-gray-700 border-gray-300',   step: 1 },
  issued:    { label: 'صادر من المخزن', cls: 'bg-blue-50 text-blue-700 border-blue-300',     step: 2 },
  received:  { label: 'مستلم بالموقع',  cls: 'bg-amber-50 text-amber-700 border-amber-300',  step: 3 },
  inspected: { label: 'فحص الجودة',     cls: 'bg-indigo-50 text-indigo-700 border-indigo-300', step: 4 },
  accepted:  { label: 'مقبول',          cls: 'bg-emerald-50 text-emerald-700 border-emerald-300', step: 5 },
  rejected:  { label: 'مرفوض',          cls: 'bg-rose-50 text-rose-700 border-rose-300',     step: -1 },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const sumLine = (mir, field) =>
  (mir.materials || []).reduce((a, m) => a + (Number(m[field]) || 0), 0);

const MIRReviewQueue = () => {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [deliveryModal, setDeliveryModal] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const path = tab === 'pending' ? '/pece/mir/pending' : '/pece/mir?limit=300';
        const r = await axiosInstance.get(path);
        if (!cancelled) setRows(r.data);
      } catch (e) {
        if (!cancelled) toast.error('فشل تحميل قائمة MIR');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [tab, reloadTick]);

  const reload = () => setReloadTick((t) => t + 1);

  const act = async (mir, endpoint, body, successMsg) => {
    setActingOn(mir.id);
    try {
      const r = await axiosInstance.post(`/pece/mir/${mir.id}/${endpoint}`, body || {});
      toast.success(`${successMsg} · ${r.data.mir_no}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الإجراء');
    } finally {
      setActingOn(null);
    }
  };

  const reject = async (mir, reason) => {
    setActingOn(mir.id);
    try {
      await axiosInstance.post(`/pece/mir/${mir.id}/reject`, { reason });
      toast.success(`تم رفض ${mir.mir_no}`);
      setRejectModal(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الرفض');
    } finally {
      setActingOn(null);
    }
  };

  const submitDelivery = async (mir, payload) => {
    setActingOn(mir.id);
    try {
      const r = await axiosInstance.post(
        `/pece/mir/${mir.id}/log-delivery`,
        payload,
      );
      const entry = r.data.delivery_log[r.data.delivery_log.length - 1];
      toast.success(`تم تسجيل ${entry.delivery_no} · اكتمال ${r.data.delivery_completion_pct}%`);
      setDeliveryModal(null);
      reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail :
                    (detail?.message || 'فشل تسجيل التسليم');
      toast.error(msg);
    } finally {
      setActingOn(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mir-review-page">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs text-cyan-600 font-bold uppercase tracking-wider mb-1 flex items-center gap-2">
                <PackageCheck className="w-4 h-4" /> MIR · Material Issue Requests
              </div>
              <h1 className="text-2xl font-bold text-gray-900">قائمة طلبات إصدار المواد للموقع</h1>
              <p className="text-sm text-gray-500 mt-1">
                تتبع حركة المواد فعلياً من المخزن إلى الموقع (إصدار → استلام → فحص → قبول).
              </p>
            </div>
            <button
              data-testid="mir-refresh-btn"
              onClick={reload}
              className="px-4 py-2.5 border border-cyan-300 text-cyan-700 rounded-lg hover:bg-cyan-50 flex items-center gap-2 font-semibold text-sm"
            >
              <RefreshCw className="w-4 h-4" /> تحديث
            </button>
          </div>

          <div className="flex gap-1 mt-5">
            <TabBtn id="pending" active={tab} onClick={setTab} icon={<ListChecks className="w-4 h-4" />}>
              قيد التنفيذ <span className="ml-2 bg-cyan-100 text-cyan-700 text-xs px-2 py-0.5 rounded-full">{tab === 'pending' ? rows.length : '·'}</span>
            </TabBtn>
            <TabBtn id="all" active={tab} onClick={setTab} icon={<Activity className="w-4 h-4" />}>
              كل MIRs
            </TabBtn>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {loading && (
          <div className="flex items-center justify-center py-20 text-gray-500" data-testid="mir-loading">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> جاري التحميل...
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center" data-testid="mir-empty">
            <PackageCheck className="w-12 h-12 mx-auto text-cyan-500 mb-3" />
            <h3 className="text-lg font-bold text-gray-800">لا توجد طلبات إصدار مواد</h3>
            <p className="text-sm text-gray-500 mt-1">
              {tab === 'pending'
                ? 'لا توجد طلبات قيد التنفيذ. يتم توليدها تلقائياً عند اعتماد MAR من الاستشاري.'
                : 'لم يتم إنشاء أي MIR بعد.'}
            </p>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="space-y-3" data-testid="mir-list">
            {rows.map((m) => (
              <MIRCard
                key={m.id}
                mir={m}
                expanded={expandedId === m.id}
                isActing={actingOn === m.id}
                onToggle={() => setExpandedId(expandedId === m.id ? null : m.id)}
                onIssue={() => act(m, 'issue', {}, 'تم الإصدار')}
                onReceive={() => act(m, 'receive', {}, 'تم الاستلام')}
                onInspect={() => act(m, 'inspect', {}, 'تم الفحص')}
                onAccept={() => act(m, 'accept', {}, 'تم القبول النهائي')}
                onReject={() => setRejectModal(m)}
                onLogDelivery={() => setDeliveryModal(m)}
              />
            ))}
          </div>
        )}
      </div>

      {rejectModal && (
        <RejectModal
          mir={rejectModal}
          onCancel={() => setRejectModal(null)}
          onConfirm={(reason) => reject(rejectModal, reason)}
          isActing={actingOn === rejectModal.id}
        />
      )}

      {deliveryModal && (
        <LogDeliveryModal
          mir={deliveryModal}
          onCancel={() => setDeliveryModal(null)}
          onConfirm={(payload) => submitDelivery(deliveryModal, payload)}
          isActing={actingOn === deliveryModal.id}
        />
      )}
    </div>
  );
};

const TabBtn = ({ id, active, onClick, children, icon }) => (
  <button
    data-testid={`mir-tab-${id}`}
    onClick={() => onClick(id)}
    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition flex items-center gap-2 ${
      active === id
        ? 'border-cyan-600 text-cyan-700'
        : 'border-transparent text-gray-600 hover:text-gray-900'
    }`}
  >
    {icon}{children}
  </button>
);

const StepBadge = ({ n, label, active, done, rejected }) => (
  <div className={`flex flex-col items-center gap-1 ${rejected ? 'opacity-40' : ''}`}>
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${
      done ? 'bg-emerald-500 text-white border-emerald-500' :
      active ? 'bg-cyan-500 text-white border-cyan-500 ring-2 ring-cyan-200' :
      'bg-white text-gray-400 border-gray-300'
    }`}>
      {done ? '✓' : n}
    </div>
    <div className="text-[9px] text-gray-600 font-medium">{label}</div>
  </div>
);

const MIRCard = ({ mir, expanded, isActing, onToggle,
                    onIssue, onReceive, onInspect, onAccept, onReject,
                    onLogDelivery }) => {
  const meta = STATUS_META[mir.status] || STATUS_META.draft;
  const step = meta.step;
  const canIssue   = mir.status === 'draft';
  const canReceive = mir.status === 'issued';
  const canInspect = mir.status === 'received';
  const canAccept  = mir.status === 'inspected';
  const canReject  = ['received', 'inspected'].includes(mir.status);
  const isRejected = mir.status === 'rejected';

  return (
    <div
      data-testid={`mir-card-${mir.id}`}
      className="bg-white rounded-xl border border-gray-200 hover:shadow-sm transition"
    >
      <div className="p-5">
        <div className="flex items-start gap-4 flex-wrap">
          {/* Status pill */}
          <div className={`px-3 py-2 rounded-lg border ${meta.cls} text-center min-w-[140px]`}>
            <PackageCheck className="w-5 h-5 mx-auto" />
            <div className="text-[10px] uppercase font-bold tracking-wider mt-1">{meta.label}</div>
          </div>

          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-mono font-bold text-base text-gray-900">{mir.mir_no}</div>
                {mir.mar_no && (
                  <div className="text-xs text-gray-500">
                    Linked MAR: <span className="font-mono font-semibold text-gray-700">{mir.mar_no}</span>
                    {mir.ppr_no && <> · PPR: <span className="font-mono">{mir.ppr_no}</span></>}
                  </div>
                )}
                {mir.activity_code && (
                  <div className="text-xs text-gray-600">
                    Activity: <span className="font-mono">{mir.activity_code}</span>
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase text-gray-500 font-semibold">Source</div>
                <div className="text-xs font-semibold text-cyan-700">
                  {mir.source === 'manual' ? 'يدوي' : 'تلقائي'}
                </div>
              </div>
            </div>

            {/* Steps strip */}
            <div className="mt-3 flex items-center gap-2 sm:gap-4">
              <StepBadge n="1" label="مسودة" done={step > 1} active={step === 1} rejected={isRejected} />
              <div className={`flex-1 h-0.5 ${step > 1 ? 'bg-emerald-400' : 'bg-gray-200'}`} />
              <StepBadge n="2" label="صادر" done={step > 2} active={step === 2} rejected={isRejected} />
              <div className={`flex-1 h-0.5 ${step > 2 ? 'bg-emerald-400' : 'bg-gray-200'}`} />
              <StepBadge n="3" label="مستلم" done={step > 3} active={step === 3} rejected={isRejected} />
              <div className={`flex-1 h-0.5 ${step > 3 ? 'bg-emerald-400' : 'bg-gray-200'}`} />
              <StepBadge n="4" label="مفحوص" done={step > 4} active={step === 4} rejected={isRejected} />
              <div className={`flex-1 h-0.5 ${step > 4 ? 'bg-emerald-400' : 'bg-gray-200'}`} />
              <StepBadge n="5" label="مقبول" done={step > 4} active={step === 5} rejected={isRejected} />
            </div>

            {/* Material summary */}
            {Array.isArray(mir.materials) && mir.materials.length > 0 && (
              <div className="mt-3 p-3 bg-cyan-50 border border-cyan-100 rounded-lg text-sm">
                <div className="flex items-center gap-2 font-semibold text-cyan-900 flex-wrap">
                  <PackageOpen className="w-4 h-4" />
                  {mir.materials[0].material_name || 'مواد'} {mir.materials.length > 1 && <span className="text-xs">+{mir.materials.length - 1}</span>}
                  {mir.supplier_name && (
                    <span className="ml-2 text-[10px] bg-white border border-cyan-300 px-2 py-0.5 rounded flex items-center gap-1">
                      <Building className="w-3 h-3" /> {mir.supplier_name}
                    </span>
                  )}
                  {mir.delivery_completion_pct > 0 && (
                    <span className={`ml-auto text-[10px] px-2 py-0.5 rounded font-bold ${
                      mir.delivery_completion_pct >= 100 ?
                        'bg-emerald-100 text-emerald-700' :
                        'bg-amber-100 text-amber-700'
                    }`}>
                      تسليم: {mir.delivery_completion_pct.toFixed(0)}%
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-cyan-800">
                  <span>طلب: <strong>{sumLine(mir, 'qty_requested')}</strong></span>
                  {sumLine(mir, 'qty_ordered') > 0 && (
                    <span>أمر شراء: <strong>{sumLine(mir, 'qty_ordered')}</strong></span>
                  )}
                  {sumLine(mir, 'qty_delivered') > 0 && (
                    <span>مُسلَّم: <strong className="text-cyan-700">{sumLine(mir, 'qty_delivered')}</strong></span>
                  )}
                  <span>صادر: <strong>{sumLine(mir, 'qty_issued')}</strong></span>
                  <span>مستلم: <strong>{sumLine(mir, 'qty_received')}</strong></span>
                  <span>مقبول: <strong className="text-emerald-700">{sumLine(mir, 'qty_accepted')}</strong></span>
                  {sumLine(mir, 'qty_rejected') > 0 && (
                    <span>مرفوض: <strong className="text-rose-700">{sumLine(mir, 'qty_rejected')}</strong></span>
                  )}
                </div>
              </div>
            )}

            {/* Rejection */}
            {isRejected && mir.rejection_reason && (
              <div className="mt-2 p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-700 flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
                <div><strong>سبب الرفض:</strong> {mir.rejection_reason}</div>
              </div>
            )}

            <div className="mt-3 text-[10px] text-gray-400">
              {fmtDate(mir.created_at)}
              {mir.correlation_id && (
                <a
                  href={`/ecp?correlation_id=${mir.correlation_id}`}
                  className="ml-3 text-cyan-700 underline hover:no-underline"
                  data-testid={`mir-ecp-link-${mir.id}`}
                >
                  عرض السلسلة في ECP →
                </a>
              )}
            </div>
          </div>

          {/* Actions column */}
          <div className="flex flex-col gap-2 min-w-[160px]">
            {canIssue && (
              <ActionBtn testId={`mir-issue-${mir.id}`} disabled={isActing}
                          onClick={onIssue} icon={<ArrowDownToLine className="w-4 h-4" />}
                          color="blue" label="إصدار من المخزن" />
            )}
            {canReceive && (
              <ActionBtn testId={`mir-receive-${mir.id}`} disabled={isActing}
                          onClick={onReceive} icon={<Truck className="w-4 h-4" />}
                          color="amber" label="استلام بالموقع" />
            )}
            {canInspect && (
              <ActionBtn testId={`mir-inspect-${mir.id}`} disabled={isActing}
                          onClick={onInspect} icon={<Microscope className="w-4 h-4" />}
                          color="indigo" label="فحص جودة" />
            )}
            {canAccept && (
              <ActionBtn testId={`mir-accept-${mir.id}`} disabled={isActing}
                          onClick={onAccept} icon={<Check className="w-4 h-4" />}
                          color="emerald" label="قبول نهائي" />
            )}
            {canReject && (
              <button
                data-testid={`mir-reject-${mir.id}`}
                disabled={isActing}
                onClick={onReject}
                className="px-3 py-2 bg-white border border-rose-300 text-rose-700 rounded-lg text-sm font-semibold hover:bg-rose-50 disabled:opacity-50 flex items-center justify-center gap-1"
              >
                <X className="w-4 h-4" /> رفض
              </button>
            )}
            {/* Log Delivery — always available until accepted/rejected */}
            {!['accepted', 'rejected'].includes(mir.status) && (
              <button
                data-testid={`mir-log-delivery-${mir.id}`}
                disabled={isActing}
                onClick={onLogDelivery}
                className="px-3 py-2 bg-white border border-cyan-300 text-cyan-700 rounded-lg text-sm font-semibold hover:bg-cyan-50 disabled:opacity-50 flex items-center justify-center gap-1"
              >
                <Truck className="w-4 h-4" /> تسجيل تسليم
              </button>
            )}
            <button
              data-testid={`mir-toggle-${mir.id}`}
              onClick={onToggle}
              className="px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs hover:bg-gray-50 flex items-center justify-center gap-1"
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              التفاصيل
            </button>
          </div>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-100 text-xs grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`mir-details-${mir.id}`}>
            <Field label="Project ID" value={mir.project_id?.slice(0, 12) + '…'} mono />
            <Field label="Reservation ID" value={mir.reservation_id ? mir.reservation_id.slice(0, 12) + '…' : '—'} mono />
            <Field label="Delivery Note" value={mir.delivery_note_ref || '—'} mono />
            <Field label="Issued" value={`${fmtDate(mir.issued_at)} · ${mir.issued_by || '—'}`} />
            <Field label="Received" value={`${fmtDate(mir.received_at)} · ${mir.received_by || '—'}`} />
            <Field label="Inspected" value={`${fmtDate(mir.inspected_at)} · ${mir.inspected_by || '—'}`} />
            <Field label="Accepted" value={`${fmtDate(mir.accepted_at)} · ${mir.accepted_by || '—'}`} />
            {mir.inspection_notes && <Field label="Inspection Notes" value={mir.inspection_notes} />}
            {mir.issue_notes && <Field label="Issue Notes" value={mir.issue_notes} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default MIRReviewQueue;

// ─── Support components ────────────────────────────────────────────────────

const ActionBtn = ({ testId, disabled, onClick, icon, color, label }) => {
  const cls = {
    blue:    'bg-blue-600 hover:bg-blue-700',
    amber:   'bg-amber-600 hover:bg-amber-700',
    indigo:  'bg-indigo-600 hover:bg-indigo-700',
    emerald: 'bg-emerald-600 hover:bg-emerald-700',
  }[color] || 'bg-gray-700 hover:bg-gray-800';
  return (
    <button
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      className={`px-3 py-2 ${cls} text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-1`}
    >
      {disabled ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}
      {label}
    </button>
  );
};

const Field = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] uppercase font-bold text-gray-500">{label}</div>
    <div className={mono ? 'font-mono text-gray-800' : 'text-gray-800'}>{value}</div>
  </div>
);

const RejectModal = ({ mir, onCancel, onConfirm, isActing }) => {
  const [reason, setReason] = useState('');
  return (
    <div
      data-testid="mir-reject-modal"
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold mb-1">رفض MIR</h3>
        <p className="text-sm text-gray-600 mb-4">
          سيتم رفض <span className="font-mono font-bold">{mir.mir_no}</span>. السبب مطلوب.
        </p>
        <textarea
          data-testid="mir-reject-reason"
          placeholder="السبب (إجباري) — مثال: مادة لا تطابق المواصفات، تلف ظاهري..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4 h-24 resize-none"
        />
        <div className="flex justify-end gap-2">
          <button
            data-testid="mir-reject-cancel"
            onClick={onCancel}
            disabled={isActing}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
          >
            إلغاء
          </button>
          <button
            data-testid="mir-reject-confirm"
            onClick={() => reason.trim() && onConfirm(reason.trim())}
            disabled={isActing || !reason.trim()}
            className="px-4 py-2 bg-rose-600 text-white rounded-lg text-sm font-semibold hover:bg-rose-700 disabled:opacity-50 flex items-center gap-1"
          >
            {isActing ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
            تأكيد الرفض
          </button>
        </div>
      </div>
    </div>
  );
};

// Log Delivery — Step 3.2 modal
const LogDeliveryModal = ({ mir, onCancel, onConfirm, isActing }) => {
  const [deliveryNoteRef, setDeliveryNoteRef] = useState('');
  const [notes, setNotes] = useState('');
  const [allowOver, setAllowOver] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [supplierName, setSupplierName] = useState(mir.supplier_name || '');
  const [supplierPo, setSupplierPo] = useState(mir.supplier_po_ref || '');
  const [lineQtys, setLineQtys] = useState(
    () => (mir.materials || []).map((m) => ({
      material_id: m.material_id,
      material_name: m.material_name,
      qty_ordered: m.qty_ordered || 0,
      already_delivered: m.qty_delivered || 0,
      qty_delivered: 0,
    })),
  );

  const totalNew = lineQtys.reduce((a, l) => a + (Number(l.qty_delivered) || 0), 0);
  const hasOver = lineQtys.some((l) =>
    (Number(l.already_delivered) + Number(l.qty_delivered)) > Number(l.qty_ordered) + 1e-6
  );

  const updateLine = (idx, val) => {
    const next = [...lineQtys];
    next[idx] = { ...next[idx], qty_delivered: val };
    setLineQtys(next);
  };

  const handleSubmit = async () => {
    // Persist supplier info first if changed and currently empty
    if ((supplierName || supplierPo) &&
        (mir.supplier_name !== supplierName || mir.supplier_po_ref !== supplierPo)) {
      try {
        await axiosInstance.patch(`/pece/mir/${mir.id}/supplier-info`, {
          delivery_mode: 'from_supplier',
          supplier_name: supplierName || null,
          supplier_po_ref: supplierPo || null,
        });
      } catch (e) {
        // non-fatal — proceed with delivery log
      }
    }
    const payload = {
      delivery_note_ref: deliveryNoteRef || null,
      notes: notes || null,
      allow_over_delivery: allowOver,
      override_reason: overrideReason || null,
      lines: lineQtys
        .filter((l) => Number(l.qty_delivered) > 0)
        .map((l) => ({
          material_id: l.material_id,
          qty_delivered: Number(l.qty_delivered),
        })),
    };
    onConfirm(payload);
  };

  return (
    <div
      data-testid="mir-delivery-modal"
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold mb-1 flex items-center gap-2">
          <Truck className="w-5 h-5 text-cyan-600" />
          تسجيل تسليم — {mir.mir_no}
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          سيتم تسجيل شحنة جديدة. لن تتغيّر حالة MIR — هذا توثيق لوجستي فقط.
        </p>

        {/* Supplier info */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <label className="text-[10px] uppercase font-bold text-gray-600">اسم المورد</label>
            <input
              data-testid="delivery-supplier-name"
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              placeholder="ACME Supplies"
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm mt-1"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-gray-600">رقم أمر الشراء</label>
            <input
              data-testid="delivery-supplier-po"
              value={supplierPo}
              onChange={(e) => setSupplierPo(e.target.value)}
              placeholder="PO-2026-0001"
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm mt-1"
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="text-[10px] uppercase font-bold text-gray-600">رقم سند التسليم</label>
          <input
            data-testid="delivery-note-ref"
            value={deliveryNoteRef}
            onChange={(e) => setDeliveryNoteRef(e.target.value)}
            placeholder="DN-12345 (اختياري)"
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm mt-1"
          />
        </div>

        {/* Per-line qty inputs */}
        <div className="mb-3">
          <div className="text-[10px] uppercase font-bold text-gray-600 mb-1">
            الكميات المُسلَّمة (لكل مادة)
          </div>
          <div className="border border-gray-200 rounded divide-y">
            {lineQtys.map((line, i) => (
              <div key={line.material_id || i} className="p-2 flex items-center gap-2 text-xs"
                   data-testid={`delivery-line-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{line.material_name || '—'}</div>
                  <div className="text-[10px] text-gray-500">
                    أمر: {line.qty_ordered} · مُسلَّم سابقاً: {line.already_delivered}
                  </div>
                </div>
                <input
                  data-testid={`delivery-line-input-${i}`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.qty_delivered}
                  onChange={(e) => updateLine(i, e.target.value)}
                  className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                />
              </div>
            ))}
          </div>
          <div className="text-[10px] text-gray-500 mt-1">
            إجمالي هذه الشحنة: <strong className="font-mono">{totalNew}</strong>
          </div>
        </div>

        {/* Over-delivery override */}
        {hasOver && (
          <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded text-xs">
            <div className="flex items-center gap-2 text-amber-800 font-semibold mb-2">
              <AlertTriangle className="w-4 h-4" />
              تنبيه: تجاوز كمية أمر الشراء في بعض المواد
            </div>
            <label className="flex items-center gap-2 mb-2">
              <input
                data-testid="delivery-allow-over"
                type="checkbox"
                checked={allowOver}
                onChange={(e) => setAllowOver(e.target.checked)}
                className="accent-amber-600"
              />
              <span>السماح بتسجيل التجاوز (controlled reality)</span>
            </label>
            {allowOver && (
              <input
                data-testid="delivery-override-reason"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="سبب التجاوز (إجباري) — مثال: المورد ضمّ pallet إضافي..."
                className="w-full px-2 py-1.5 border border-amber-400 rounded text-sm bg-white"
              />
            )}
          </div>
        )}

        <textarea
          data-testid="delivery-notes"
          placeholder="ملاحظات إضافية (اختياري)..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4 h-16 resize-none"
        />

        <div className="flex justify-end gap-2">
          <button
            data-testid="delivery-cancel"
            onClick={onCancel}
            disabled={isActing}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
          >
            إلغاء
          </button>
          <button
            data-testid="delivery-confirm"
            onClick={handleSubmit}
            disabled={isActing || totalNew <= 0 || (hasOver && (!allowOver || !overrideReason.trim()))}
            className="px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm font-semibold hover:bg-cyan-700 disabled:opacity-50 flex items-center gap-1"
          >
            {isActing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            تسجيل الشحنة
          </button>
        </div>
      </div>
    </div>
  );
};
